/**
 * Predefined page types. These indicate the type of each page, 
 * which determines the specific React Router controls they perform. 
 * Components added within these pages rely on these router controls, 
 * making it essential to match the correct components with the correct page types. 
 * These values are static and must be updated simultaneously on the back-end, 
 * back office, and site when a new type needs to be added.
 */
export enum PageTypes {
  GENERIC                   = "generic",
  HOME                      = "home",
  CASINO                    = "casino",
  LIVE_CASINO               = "live-casino",
  GAME_LIST                 = "game-list",
  TOURNAMENTS               = "tournaments",
  PROMOTIONS                = "promotions",
  HELP                      = "help"
}

export enum Components {

  PROMOTION_TABS                    = "promotion-tabs",
  PROMOTIONS_GRID                   = "promotions-grid",
  GAMES_GRID                        = "games-grid",
  GAME_CATEGORIES                   = "game-categories",
  GAME_PROVIDERS                    = "game-providers",
  FOUR_LAYOUT_BOXES                 = "four-layout-boxes",
  SIX_LAYOUT_BOXES                  = "six-layout-boxes",
  FEATURED_GAMES                    = "featured-games",
  INDEPENDENTS_GAMES_GRID           = "independents-games-grid",
  SPACER                            = "spacer",
  SLIDER                            = "slider",
  CASINO_FEATURED                   = "casino-featured",
  MULTIPLE_JACKPOTS                 = "multiple-jackpots",
  IMAGE                             = "image",
  RESULT                            = "result",
  HELP                              = "help",

  MOBILE_SLIDER                     = "mobile-slider",
  MOBILE_SPACER                     = "mobile-spacer",
  MOBILE_SUB_PAGE_HEADER            = "mobile-sub-page-header",
  MOBILE_TWO_LAYOUT_BOXES           = "mobile-two-layout-boxes",
  MOBILE_FOUR_LAYOUT_BOXES          = "mobile-four-layout-boxes",
  MOBILE_EIGHT_LAYOUT_BOXES         = "mobile-eight-layout-boxes",
  MOBILE_GAMES_GRID                 = "mobile-games-grid",
  MOBILE_INDEPENDENTS_GAMES_GRID    = "mobile-independents-games-grid",
  MOBILE_GAME_CAROUSEL              = "mobile-game-carousel",
  MOBILE_GAME_NAVIGATOR             = "mobile-game-navigator",
  MOBILE_GAME_SEARCH                = "mobile-game-search",
  MOBILE_GAME_CATEGORIES            = "mobile-game-categories",
  MOBILE_GAME_FILTER                = "mobile-game-filter",
  MOBILE_PROMOTIONS_GRID            = "mobile-promotions-grid",
  MOBILE_PROMOTION_TABS             = "mobile-promotion-tabs",
  MOBILE_MULTIPLE_JACKPOTS          = "mobile-multiple-jackpots",
  MOBILE_IMAGE                      = "mobile-image",
  MOBILE_HELP                       = "mobile-help",
  MOBILE_RESULT                     = "mobile-result",

  NOT_FOUND                         = "not-found"
  
}

/**
 * Predefined action types. These indicate the type of a clickable item. 
 * These values are static and must be updated simultaneously on the back-end, 
 * back office, and site when a new type needs to be added.
 */
export enum ActionTypes {
  URL                         = "url",
  PAGE                        = "page",
  GAME                        = "game",
  POPUP                       = "popup",
  PROMOTION                   = "promotion",
  SYSTEM_ACTIONS              = "system-actions"
}

/**
 * Predefined system actions. These are used to display the selected action
 * on the site according to the action chosen in the back office.
 */
export enum SystemActions {
  DEPOSIT                     = "deposit",
  WITHDRAW                    = "withdraw",
  BONUS_REQUEST               = "bonus-request",
  VIP                         = "vip",
  SIGN_IN                     = "sign-in",
  SIGN_UP                     = "sign-up",
  LIVE_SUPPORT                = "live-support",
  MEGA_MENU                   = "mega-menu"
}

export enum EnvironmentTypes {
  PRODUCTION                  = "production",
  STAGING                     = "staging",
  DEVELOPMENT                 = "development"
}

export enum DeviceTypes {
  MOBILE                      = "mobile",
  TABLET                      = "tablet",
  DESKTOP                     = "desktop",
  TOUCH                       = "touch"
}

export enum MegaMenuTypes {
  NONE                        = "",
  SINGLE                      = "single",
  COLLAPSIBLE                 = "collapsible",
  OPEN_GROUP                  = "open-group",
}

export enum DateFormats {
  MOMENT                      = "Moment",
  DATE                        = "Date",
  TIME                        = "Time",
  FULL_DATE                   = "FullDate",
  HUMAN_DATE                  = "HumanDate",
  DATE_TIME                   = "DateTime"
}

export enum PopupTypes {
  REGISTRATION              = "registration",
  RUNTIME                   = "runtime",
  PAGE                      = "page",
}

export enum WithdrawActionsTypes {
  SHOW_WITHDRAW_METHOD        = "show-withdraw-method",
  ADD_WITHDRAW_ACCOUNT        = "add-withdraw-account",
  EDIT_WITHDRAW_ACCOUNT       = "edit-withdraw-account",
  REMOVE_WITHDRAW_ACCOUNT     = "remove-withdraw-account"
}

export enum PaymentTypes {
  DEPOSIT                     = "deposit",
  WITHDRAW                    = "withdraw"
}

export enum PaymentStatusTypes {
  OPEN                        = "open",
  PENDING                     = "pending",
  APPROVED                    = "approved",
  DECLINED                    = "declined",
  TIMEOUT                     = "time-out",
  CANCELLED                   = "cancelled",
  VOIDED                      = "voided"
}

export enum PaymentCategoryTypes {
  DEPOSIT                     = "deposit",
  WITHDRAW                    = "withdraw",
}

export enum GameCategoryTypes {
  CASINO                      = "casino",
  LIVE_CASINO                 = "live-casino",
  VIRTUAL_GAMES               = "virtual-casino",
  UP_N_DOWN                   = "up-n-down",
  SPORTS                      = "sports"
}

export enum ProviderIds {
  EGT                         = "egt-digital",
  PRAGMATIC_PLAY              = "pragmatic-play-slot",
  AMUSNET                     = "amusnet"
}

export enum JackpotsIds {
  AMUSNET_JACKPOT             = "amu|amusnet-jackpot",
  EGYPT_QUEST                 = "amu|egypt-quest",
  HIGH_CASH                   = "egt|high-cash",
  BELL_LINK                   = "egt|bell-link",
  CLOVER_CHANCE               = "egt|clover-chance",
  RISE_OF_RA                  = "egt|rise-of-ra",
  VERSALLES_GOLD              = "egt|versailles-gold",
  BURNING_HOT                 = "egt|burning-hot",
  SUPER_HOT_20                = "egt|20-super-hot",
  SUPER_HOT_40                = "egt|40-super-hot",
  JACKPOT_PLAY                = "pps|inntech-jackpotplay-test"
}

export enum TransactionReasonsTypes {
  ERROR                       = "error",
  BET                         = "bet",
  BUY_FEATURE                 = "buy-feature",
  FREE_SPIN                   = "free-spin",
  WON                         = "won",
  JACKPOT                     = "jackpot",
  LOST                        = "lost",
  VOIDED                      = "voided",
  WON_VOIDED                  = "won-voided",
  LOST_VOIDED                 = "lost-voided",
  REFUND                      = "refund",
  OFFLINE_WON                 = "offline-won",
  DEPOSIT                     = "deposit",
  WITHDRAW                    = "withdraw",
  BALANCE_CORRECTION          = "balance-correction",
  ATM_DEDUCTION               = "atm-deduction",
  BONUS_CORRECTION            = "bonus-correction",
  GAME_ISSUE                  = "game-issue",
  BONUS_APPLY                 = "bonus-apply",
  BONUS_WAGERING_TIMEOUT      = "bonus-wagering-timeout",
  BONUS_INSUFFICIENT_BALANCE  = "bonus-insufficient-balance",
  BONUS_PROFIT                = "bonus-profit",
  BONUS_CASHBACK              = "bonus-cashback",
  BONUS_CANCEL                = "bonus-cancel"
}

export enum PlatformTypes {
  DESKTOP                   = "desktop",
  MOBILE                    = "mobile"
}

export enum BonusGameCategoryTypes {
  FREE                        = "free",
  SPORTS                      = "sports",
  CASINO                      = "casino"
}

export enum DepositTypes {
  MANUAL                      = "manual",
  DEPOSIT                     = "deposit",
  LOSS                        = "loss",
  FREE_SPIN                   = "free-spin"
}

export enum DepositListTypes {
  METHOD                      = "method",
  GROUP                       = "group"
}

export enum CreatorTypes {
  USER                        = "user",
  ADMIN                       = "admin"
}

export enum GameHistoryStatusTypes {
  WON                         = "won",
  LOST                        = "lost",
  BET                         = "bet",
  REFUNDED                    = "refunded",
  CANCELLED                   = "cancelled"
}

export enum BonusStatusTypes {
  ACTIVE                      = "active",
  OPEN                        = "open",
  DECLINED                    = "declined",
  CANCELLED                   = "cancelled",
  LOST_BY_TIMEOUT             = "lost-by-timeout",
  LOST_BY_BALANCE             = "lost-by-balance",
  COMPLETED                   = "completed",
  PENDING                     = "pending",
  EXPIRED                     = "expired",
  REQUESTABLE                 = "requestable",
  ACTIVATABLE                 = "activatable"
}

export enum FreeSpinStatusTypes {
  APPLIED                     = "applied",
  REJECTED                    = "rejected",
  CANCELLED                   = "cancelled",
  ACCEPTED                    = "accepted",
  COMPLETED                   = "completed",
  TIMEOUT                     = "time-out"
}

export enum SortingTypes {
  POPULAR                     = "popular",
  ASC                         = "asc",
  DESC                        = "desc",
  PROVIDER                    = "provider"
}

export enum ImageTypes {
  GAMES                       = "games",
  FEATURED_BOX                = "featured-box",
  PROMOTION                   = "promotion",
  FREE                        = "free",
  CASINO_FEATURED             = "casino-featured",
  TWO_LAYOUT_BOXES            = "two-layout-boxes",
  FOUR_LAYOUT_BOXES           = "four-layout-boxes",
  EIGHT_LAYOUT_BOXES          = "eight-layout-boxes",
}

export enum LinkTypes {
  LINK                        = "link",
  CLICK                       = "click"
}

export enum ThemeTypes {
  DARK                        = "dark",
  LIGHT                       = "light"
}

export enum UserTypes {
  ALL                         = "",
  GUEST                       = "guest",
  LOGGED_IN                   = "logged-in"
}

export enum TargetTypes {
  SELF                        = "_self",
  BLANK                       = "_blank",
  TOP                         = "_top"
}

export enum BlockTypes {
  BREAK_LINE                  = "breakLine",
  PARAGRAPH                   = "paragraph",
  TABLE                       = "table",
  IMAGE                       = "image",
  LIST                        = "list",
  HEADER                      = "header",
  RAW_TOOL                    = "rawTool"
}

export enum LiveSupportTypes {
  COMM100                     = "comm100"
}

export enum LicenseTypes {
  CODE  = "code",
  IMAGE = "image"
}

/**
 * 
 * API Predefined Field Values
 * 
 * **TOKEN**
 * Indicates a warning related to the X-Auth-Token value sent in the request header. This usually signifies that the session has ended.
 * 
 * **USER**
 * Refers to a data blockage or issue related to the session’s user in the Site API responses.
 * 
 * **USER_SETTINGS**
 * Refers to a data blockage or issue related to the session’s user settings in the Site API responses.
 * 
 * **PASSWORD**
 * Refers to a data blockage or issue related to the session’s user password in the Site API responses.
 * 
 * **ADMIN**
 * Refers to a data blockage or issue related to the session’s user in the Backoffice API responses.
 * 
 * **PRIVACY_POLICY**
 * Indicates that the session’s user needs to approve new rules in the Site API responses.
 * 
 * @description These are the predefined field values that can be returned by the API.
 * 
 * @see https://staging-backoffice.inntech.ae/api/docs/index.html
 * 
 */
export enum APIPredefinedFieldValues {
  TOKEN                             = "TOKEN",
  USER                              = "USER",
  USER_SETTINGS                     = "USER_SETTINGS",
  PASSWORD                          = "PASSWORD",
  ADMIN                             = "ADMIN",
  PRIVACY_POLICY                    = "PRIVACY_POLICY"
}

/**
 * 
 * API Predefined Code Values
 * 
 * **REQUIRED**
 * Indicates that the field is mandatory.
 * 
 * **EXPIRED**
 * Indicates that the item associated with the field is no longer valid.
 * 
 * **INVALID**
 * Indicates that the content of the field is invalid/incorrect.
 * 
 * **NOT_IN_RANGE**
 * Indicates that the content of the field is not in allowed range.
 * 
 * **EXISTS**
 * Indicates that the record attempted to be created related to the field already exists.
 * 
 * **NOT_ACTIVE**
 * Indicates that the item associated with the field is inactive for some reason.
 * 
 * **SUSPENDED**
 * Indicates that the operations related to the item associated with the field have been suspended.
 * 
 * **LIMIT_EXCEEDED**
 * Indicates that the operation related to the field has hit a single operation limit.
 * 
 * **DAILY_LIMIT_EXCEEDED**
 * Indicates that the operation related to the field has hit a daily limit.
 * 
 * **WEEKLY_LIMIT_EXCEEDED**
 * Indicates that the operation related to the field has hit a weekly limit.
 * 
 * **MONTHLY_LIMIT_EXCEEDED**
 * Indicates that the operation related to the field has hit a monthly limit.
 * 
 * **YEARLY_LIMIT_EXCEEDED**
 * Indicates that the operation related to the field has hit a yearly limit.
 * 
 * **IN_USE**
 * Indicates that the operation related to the field is blocked by another record or operation (indicating a dependency).
 * 
 * @description These are the predefined code values that can be returned by the API.
 * 
 * @see https://staging-backoffice.inntech.ae/api/docs/index.html
 * 
 */
export enum APIPredefinedCodeValues {
  LIMIT_EXCEEDED                    = "LIMIT_EXCEEDED",
  REQUIRED                          = "REQUIRED",
  EXPIRED                           = "EXPIRED",
  INVALID                           = "INVALID",
  NOT_IN_RANGE                      = "NOT_IN_RANGE",
  EXISTS                            = "EXISTS",
  NOT_ACTIVE                        = "NOT_ACTIVE",
  SUSPENDED                         = "SUSPENDED",
  DAILY_LIMIT_EXCEEDED              = "DAILY_LIMIT_EXCEEDED",
  WEEKLY_LIMIT_EXCEEDED             = "WEEKLY_LIMIT_EXCEEDED",
  MONTHLY_LIMIT_EXCEEDED            = "MONTHLY_LIMIT_EXCEEDED",
  YEARLY_LIMIT_EXCEEDED             = "YEARLY_LIMIT_EXCEEDED",
  IN_USE                            = "IN_USE"
}